import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gavel, Percent, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { money, minNextBid } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Countdown } from "@/components/Countdown";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const BidForm = ({ l, onBid }) => {
  const { user, login } = useAuth();
  const min = minNextBid(l);
  const [amount, setAmount] = useState(min);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setAmount(min); }, [min]);

  if (!user) return <Button data-testid="bid-login-btn" onClick={login} className="w-full rounded-lg bg-brand hover:bg-brand-dark h-12">سجّل الدخول للمزايدة</Button>;
  if (l.is_owner) return <div className="text-sm text-slate-500 bg-slate-50 rounded-xl p-3" data-testid="bid-owner-note">هذا مزادك، لا يمكنك المزايدة عليه.</div>;
  if (!user.is_admin && user.role !== "buyer") return <div className="text-sm bg-amber-50 text-amber-800 rounded-xl p-3" data-testid="bid-not-buyer-note">المزايدة متاحة لحسابات المشترين فقط. <Link to="/dashboard" className="font-bold underline">غيّر نوع حسابك</Link></div>;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/listings/${l.id}/bids`, { amount: Number(amount) });
      toast.success("تمت المزايدة بنجاح");
      onBid();
    } catch (e) { toast.error(errMsg(e)); onBid(); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2">
        {[1, 2, 5].map((m) => (
          <button key={m} data-testid={`quick-bid-${m}`} onClick={() => setAmount(min + l.min_increment * (m - 1))}
            className="border border-slate-200 rounded-lg py-2 text-xs font-bold text-slate-700 hover:border-slate-400 tabular-nums transition-colors">
            +{(l.min_increment * m).toLocaleString("en-US")}
          </button>
        ))}
      </div>
      <Input data-testid="bid-amount-input" type="number" min={min} value={amount} onChange={(e) => setAmount(e.target.value)} className="h-12 rounded-xl text-lg font-bold" dir="ltr" />
      <div className="text-xs text-slate-500">أقل مزايدة: <b>{money(min)}</b> — عمولة الموقع عند الفوز: <b className="text-[#C2410C]">{money(Number(amount || 0) * 0.01)}</b></div>
      <Button data-testid="place-bid-btn" disabled={busy} onClick={submit} className="w-full h-12 rounded-lg bg-sun hover:bg-sun-dark text-base font-bold"><Gavel size={16} className="ml-1" />زايد الآن</Button>
    </div>
  );
};

export const BidPanel = ({ l, onBid }) => (
  <div className="bg-white border border-slate-200 shadow-lg rounded-xl p-5 space-y-5" data-testid="bid-panel">
    <div>
      {!l.is_ended && <div className="text-xs text-slate-500 mb-2">الوقت المتبقي</div>}
      <Countdown end={l.end_time} big testId="detail-countdown" />
    </div>
    <div className="flex items-end justify-between">
      <div>
        <div className="text-xs text-slate-500">{l.current_bid ? "أعلى مزايدة حالية" : "سعر الافتتاح"}</div>
        <div className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="current-bid">{money(l.current_bid || l.start_price)}</div>
      </div>
      <div className="text-xs text-slate-500">{l.bids_count || 0} مزايدة</div>
    </div>
    <div className="flex items-center gap-2 bg-[#FFF7ED] text-[#C2410C] rounded-xl p-3 text-sm font-semibold" data-testid="commission-box">
      <Percent size={16} />عمولة الموقع 1%{l.current_bid ? `: ${money(l.commission)}` : " من سعر البيع النهائي"}
    </div>
    {l.is_ended
      ? <div className="flex items-center gap-2 bg-slate-50 rounded-xl p-3 text-sm" data-testid="auction-ended-note"><Trophy size={16} className="text-sun" />{l.current_bid ? `بيع بمبلغ ${money(l.current_bid)}` : "انتهى المزاد بدون مزايدات"}</div>
      : <BidForm l={l} onBid={onBid} />}
  </div>
);

export const BidHistory = ({ bids }) => (
  <div className="bg-white border rounded-xl p-5" data-testid="bid-history">
    <h3 className="font-bold mb-4">سجل المزايدات</h3>
    {bids.length === 0 ? <p className="text-sm text-slate-500">لا توجد مزايدات بعد، كن أول المزايدين.</p> : (
      <ul className="divide-y">
        {bids.map((b, i) => (
          <li key={b.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`bid-row-${i}`}>
            <span className="flex items-center gap-2">{i === 0 && <Trophy size={14} className="text-sun" />}{b.bidder_display}</span>
            <span className="font-bold tabular-nums">{money(b.amount)}</span>
            <span className="text-xs text-slate-400" dir="ltr">{new Date(b.created_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);
